import { useState, useEffect, useMemo, useCallback } from "react";
import DraggableModal from "./DraggableModal";
import SearchSelect from "./SearchSelect";
import { PhoneField, PHONE_COUNTRIES, parsePhone as parsePhoneShared, serializePhone } from "./PhoneField";
import { loadFromSupabase } from "../supabaseDB";
import { listAllProfiles, updateProfile, setUserActive } from "../userProfile";
import { supabase } from "../supabaseClient";
import { PERMISSIONS, defaultsForRole } from "../permissions";

/* ═══════════════════════════════════════════════════════════════════════════
   USER MANAGEMENT — admin-only console for user_profiles + auth.users.
   Single source of truth for who has access and at what role.

   Create flow uses adminCreateUser() which spins a sandbox Supabase client
   for the signUp call, then PATCHes the auto-created profile through the
   main (admin-authenticated) client. The admin's session never moves.

   Edit / deactivate flows are direct UPDATE calls — RLS scopes them to
   admins via the up_update_admin policy. The DB trigger
   enforce_profile_update() raises if a non-admin somehow gets here.
   ═══════════════════════════════════════════════════════════════════════════ */

const T = {
  en: {
    title: "User Management",
    subtitle: "Manage who can access GFI Nexus and the Shipper Portal.",
    addUser: "+ Invite User",
    refresh: "Refresh",
    columns: {
      name: "Name", email: "Email", role: "Role",
      supplier: "Supplier", phone: "Phone", status: "Status", actions: "Actions",
    },
    role_admin: "Admin", role_shipper: "Shipper",
    role_logistica: "Logistics", role_operacion: "Operations", role_gerencial: "Management", role_compliance: "Compliance",
    active: "Active", inactive: "Inactive",
    edit: "Edit", deactivate: "Deactivate", activate: "Activate",
    addTitle: "Invite User", editTitle: "Edit User",
    section_account: "Account", section_role: "Role", section_contact: "Contact",
    f_email: "Email", f_email_sub: "User signs in with this email.",
    f_firstName: "First name", f_lastName: "Last name",
    f_role: "Role", f_supplier: "Supplier", f_supplierSub: "Required for all non-admin roles.",
    f_phone: "Phone", f_phoneSub: "Optional.",
    save: "Save", cancel: "Cancel", saving: "Saving…",
    inviteUser: "Invite User",
    sendInvitation: "Send Invitation",
    invitationSent: "Invitation sent successfully",
    invitationUpdated: "Invitation updated",
    deleteUser: "Delete",
    confirmDelete: "Permanently delete this user? This cannot be undone.",
    userDeleted: "User deleted",
    permissions: "Permissions",
    permissionsSub: "Granular access control. Defaults match the user's role; you can override individual switches.",
    resetToDefaults: "Reset to role defaults",
    permissionsSaved: "Permissions updated",
    pendingInvitations: "Pending Invitations",
    noInvitations: "No pending invitations",
    revokeInvitation: "Revoke",
    invitationRevoked: "Invitation revoked",
    confirmRevoke: "Revoke this invitation? The link will stop working.",
    expiresAt: "Expires",
    sentAt: "Sent",
    confirmDeactivate: "Deactivate this user? They keep their account but cannot sign in until reactivated.",
    confirmActivate: "Reactivate this user?",
    noUsers: "No users yet.",
    emailRequired: "Email is required.",
    nameRequired: "First and last name are required.",
    supplierRequired: "Select the supplier this user represents.",
    emailTakenUser: "A user with this email already exists.",
    emailTakenInvite: "There's already a pending invitation for this email.",
    updated: "User updated.",
    err_prefix: "Error: ",
    loading: "Loading…",
    you: "(you)",
    setPwdTitle: "Set password",
    sendResetTitle: "Send reset link",
    promptNewPassword: "New password for this user (min 8 chars: uppercase, lowercase, number and special character):",
    pwdWeak: "Password must be at least 8 characters with an uppercase, lowercase, number and special character.",
    passwordSet: "Password updated.",
    confirmSendReset: "Send a password-reset email to this user?",
    resetEmailSent: "Reset email sent.",
    noUsernameReset: "This user has no username yet, so the reset email can't be sent.",
  },
  es: {
    title: "Gestión de Usuarios",
    subtitle: "Administra quién puede acceder a GFI Nexus y al Portal del Exportador.",
    addUser: "+ Invitar Usuario",
    refresh: "Actualizar",
    columns: {
      name: "Nombre", email: "Correo", role: "Rol",
      supplier: "Proveedor", phone: "Teléfono", status: "Estado", actions: "Acciones",
    },
    role_admin: "Admin", role_shipper: "Shipper",
    role_logistica: "Logística", role_operacion: "Operaciones", role_gerencial: "Gerencial", role_compliance: "Cumplimiento",
    active: "Activo", inactive: "Inactivo",
    edit: "Editar", deactivate: "Desactivar", activate: "Activar",
    addTitle: "Invitar Usuario", editTitle: "Editar Usuario",
    section_account: "Cuenta", section_role: "Rol", section_contact: "Contacto",
    f_email: "Correo electrónico", f_email_sub: "El usuario inicia sesión con este correo.",
    f_firstName: "Nombre", f_lastName: "Apellido",
    f_role: "Rol", f_supplier: "Proveedor", f_supplierSub: "Requerido para todos los roles excepto Admin.",
    f_phone: "Teléfono", f_phoneSub: "Opcional.",
    save: "Guardar", cancel: "Cancelar", saving: "Guardando…",
    inviteUser: "Invitar Usuario",
    sendInvitation: "Enviar Invitación",
    invitationSent: "Invitación enviada exitosamente",
    invitationUpdated: "Invitación actualizada",
    deleteUser: "Eliminar",
    confirmDelete: "¿Eliminar permanentemente este usuario? Esta acción no se puede deshacer.",
    userDeleted: "Usuario eliminado",
    permissions: "Permisos",
    permissionsSub: "Control de acceso granular. Los valores por defecto vienen del rol del usuario; puedes ajustar cada permiso individualmente.",
    resetToDefaults: "Restablecer valores por defecto",
    permissionsSaved: "Permisos actualizados",
    pendingInvitations: "Invitaciones Pendientes",
    noInvitations: "No hay invitaciones pendientes",
    revokeInvitation: "Revocar",
    invitationRevoked: "Invitación revocada",
    confirmRevoke: "¿Revocar esta invitación? El enlace dejará de funcionar.",
    expiresAt: "Expira",
    sentAt: "Enviado",
    confirmDeactivate: "¿Desactivar este usuario? Mantiene su cuenta pero no podrá iniciar sesión hasta reactivarlo.",
    confirmActivate: "¿Reactivar este usuario?",
    noUsers: "Sin usuarios todavía.",
    emailRequired: "El correo es obligatorio.",
    nameRequired: "Nombre y apellido son obligatorios.",
    supplierRequired: "Selecciona el proveedor que representa este usuario.",
    emailTakenUser: "Ya existe un usuario con este correo.",
    emailTakenInvite: "Ya hay una invitación pendiente con este correo.",
    updated: "Usuario actualizado.",
    err_prefix: "Error: ",
    loading: "Cargando…",
    you: "(tú)",
    setPwdTitle: "Nueva clave",
    sendResetTitle: "Enviar enlace",
    promptNewPassword: "Nueva contraseña para este usuario (mín. 8 caracteres: mayúscula, minúscula, número y carácter especial):",
    pwdWeak: "La contraseña debe tener al menos 8 caracteres, con mayúscula, minúscula, número y carácter especial.",
    passwordSet: "Contraseña actualizada.",
    confirmSendReset: "¿Enviar un correo de restablecimiento de contraseña a este usuario?",
    resetEmailSent: "Correo de restablecimiento enviado.",
    noUsernameReset: "Este usuario aún no tiene username, así que no se puede enviar el correo de reset.",
  },
};

const inp = {
  padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)",
  borderRadius: 5, background: "var(--color-bg-input)", color: "var(--color-text)",
  outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box",
};
const errS = { borderColor: "var(--color-danger)" };

function Field({ label, sub, required, error, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)" }}>
        {label}{required && <span style={{ color: "var(--color-danger)", marginLeft: 3 }}>*</span>}
      </label>
      {children}
      {sub && !error && <span style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{sub}</span>}
      {error && <span style={{ fontSize: 11, color: "var(--color-danger)" }}>{error}</span>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-muted)" }}>{title}</div>
      {children}
    </div>
  );
}

function RoleBadge({ role, t }) {
  /* Legacy `shipper` rows still exist in user_profiles even though the
     invite flow no longer creates new ones — keep a fallback config so
     they render with the original violet styling. */
  var config = {
    admin:     { bg: "rgba(91,188,176,0.15)",  color: "#3D9B8F", label: t.role_admin },
    shipper:   { bg: "rgba(124,58,237,0.13)",  color: "#7C3AED", label: t.role_shipper },
    logistica: { bg: "rgba(59,130,246,0.15)",  color: "#2563EB", label: t.role_logistica },
    operacion: { bg: "rgba(168,85,247,0.15)",  color: "#7C3AED", label: t.role_operacion },
    gerencial: { bg: "rgba(245,158,11,0.15)",  color: "#D97706", label: t.role_gerencial },
    compliance: { bg: "rgba(6,182,212,0.15)",  color: "#0891B2", label: t.role_compliance },
  };
  var c = config[role] || { bg: "#F3F4F6", color: "#374151", label: role };
  return (
    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, background: c.bg, color: c.color, textTransform: "uppercase", letterSpacing: "0.5px" }}>{c.label}</span>
  );
}

function StatusBadge({ active, t }) {
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700,
      background: active ? "rgba(16,185,129,0.13)" : "rgba(220,38,38,0.13)",
      color: active ? "#059669" : "#DC2626",
      letterSpacing: "0.04em",
    }}>{active ? t.active : t.inactive}</span>
  );
}

function dialToCountryCode(d) {
  var withPlus = "+" + String(d || "").replace(/\D/g, "");
  var hit = PHONE_COUNTRIES.find(function (c) { return c.dial === withPlus; });
  return hit ? hit.code : "MX";
}
function countryCodeToDialDigits(code) {
  var hit = PHONE_COUNTRIES.find(function (c) { return c.code === code; });
  return hit ? hit.dial.replace(/\D/g, "") : "52";
}

/* ───────────────────────────────────────────────────────────────────────────
   UserForm — Add (invitation) + Edit flows share this form. Add mode
   no longer creates an auth user directly; it inserts a row into
   user_invitations and triggers an email with a registration link.
   Edit mode still PATCHes the profile in place.
   ─────────────────────────────────────────────────────────────────────────── */
function UserForm({ user, invitation, suppliers, t, lang, onSave, onCancel }) {
  var isEdit = !!user;
  var isEditInv = !!invitation;
  var [form, setForm] = useState(function () {
    if (isEditInv) return {
      email: invitation.email || "",
      firstName: invitation.first_name || "",
      lastName: invitation.last_name || "",
      role: invitation.role || "logistica",
      supplierId: invitation.supplier_id || "",
      phoneCountry: "52", phoneArea: "", phoneNumber: "",
      permissions: defaultsForRole(invitation.role || "logistica"),
    };
    if (isEdit) return {
      email: user.email || "",
      firstName: user.first_name || "",
      lastName: user.last_name || "",
      role: user.role || "logistica",
      supplierId: user.supplier_id || "",
      phoneCountry: user.phone_country_code || "",
      phoneArea: user.phone_area_code || "",
      phoneNumber: user.phone_number || "",
      /* Preserve any existing overrides; otherwise fall back to the
         role's default map so the checkbox UI has something to render. */
      permissions: (user && user.permissions && Object.keys(user.permissions).length > 0)
        ? user.permissions
        : defaultsForRole(user && user.role),
    };
    return {
      email: "", firstName: "", lastName: "",
      role: "logistica", supplierId: "",
      phoneCountry: "52", phoneArea: "", phoneNumber: "",
      permissions: defaultsForRole("logistica"),
    };
  });
  var [errors, setErrors] = useState({});
  var [busy, setBusy] = useState(false);
  var [errBanner, setErrBanner] = useState("");

  var set = function (k, v) { setForm(function (p) { return Object.assign({}, p, { [k]: v }); }); };

  var validate = function () {
    var e = {};
    if (!isEdit && !isEditInv && !form.email.trim()) e.email = t.emailRequired;
    if (!form.firstName.trim() || !form.lastName.trim()) e.name = t.nameRequired;
    /* supplier is required for every non-admin role — including legacy
       'shipper' rows still in user_profiles. */
    if (form.role !== "admin" && !form.supplierId) e.supplier = t.supplierRequired;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  async function sendInvitation() {
    var emailNorm = form.email.trim().toLowerCase();

    /* Guard against duplicate identities BEFORE inserting anything.
       Username is picked later at /register (validated there), so the
       only identity we can verify at invite time is the real email: it
       must not already belong to a user, nor to a still-pending
       invitation (revoked ones are fine to reuse). We pull the email
       columns and compare lowercased in JS — this sidesteps both the
       ilike wildcard chars (_ %) and any mixed-case legacy rows. The
       user/invitation tables are tiny so the cost is negligible. */
    var profRes = await supabase.from("user_profiles").select("email");
    if (profRes.error) throw new Error(profRes.error.message);
    if ((profRes.data || []).some(function (r) { return r.email && r.email.trim().toLowerCase() === emailNorm; })) {
      throw new Error(t.emailTakenUser);
    }
    var invRes = await supabase.from("user_invitations").select("email").eq("status", "pending");
    if (invRes.error) throw new Error(invRes.error.message);
    if ((invRes.data || []).some(function (r) { return r.email && r.email.trim().toLowerCase() === emailNorm; })) {
      throw new Error(t.emailTakenInvite);
    }

    /* Cryptographically random 64-hex-char token. crypto.getRandomValues
       is universally available in modern browsers — no fallback needed. */
    var arr = new Uint8Array(32);
    crypto.getRandomValues(arr);
    var token = Array.from(arr).map(function (b) { return b.toString(16).padStart(2, "0"); }).join("");

    var sessionRes = await supabase.auth.getUser();
    var currentUser = sessionRes && sessionRes.data && sessionRes.data.user;

    var supplierName = "";
    if (form.supplierId) {
      var sup = (suppliers || []).find(function (s) { return s._uuid === form.supplierId || s.id === form.supplierId; });
      if (sup) supplierName = sup.shortName || sup.fullName || sup.name || "";
    }

    var insertRes = await supabase.from("user_invitations").insert({
      token: token,
      email: form.email.trim().toLowerCase(),
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      role: form.role,
      supplier_id: form.role === "admin" ? null : (form.supplierId || null),
      invited_by: currentUser ? currentUser.id : null,
    });
    if (insertRes.error) throw new Error("Could not create invitation: " + insertRes.error.message);

    var res = await fetch("/.netlify/functions/send-invitation", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: form.email.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        role: form.role,
        supplierName: supplierName,
        token: token,
      }),
    });
    var data = await res.json().catch(function () { return {}; });
    if (!res.ok) {
      /* Roll back the invitation row so the admin can retry without
         hitting a unique-token conflict or leaving an orphan. */
      await supabase.from("user_invitations").delete().eq("token", token);
      throw new Error("Email send failed: " + (data.error || ("HTTP " + res.status)));
    }
    return { token: token };
  }

  var save = async function () {
    if (!validate()) return;
    setBusy(true);
    setErrBanner("");
    try {
      if (isEditInv) {
        var invPatch = {
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          role: form.role,
          supplier_id: form.role === "admin" ? null : (form.supplierId || null),
        };
        var invRes = await supabase.from("user_invitations").update(invPatch).eq("id", invitation.id);
        if (invRes.error) throw new Error(invRes.error.message);
        onSave({ kind: "invitationUpdated" });
      } else if (isEdit) {
        var patch = {
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          role: form.role,
          supplier_id: form.role === "admin" ? null : (form.supplierId || null),
          phone_country_code: form.phoneCountry || null,
          phone_area_code: form.phoneArea || null,
          phone_number: form.phoneNumber || null,
          /* Admin gets {} since hasPermission short-circuits; everyone
             else gets the explicit map the form built. */
          permissions: form.role === "admin" ? {} : (form.permissions || {}),
        };
        await updateProfile(user.id, patch);
        onSave({ kind: "updated" });
      } else {
        await sendInvitation();
        onSave({ kind: "invited" });
      }
    } catch (e) {
      setErrBanner((e && e.message) || String(e));
    } finally {
      setBusy(false);
    }
  };

  /* user_profiles.supplier_id is a UUID FK to suppliers(id) — the
     actual Postgres uuid. The supabaseDB layer presents each supplier
     row with `s.id` as the legacy string id and `s._uuid` as the real
     uuid. The dropdown MUST emit the uuid so the UPDATE / INSERT
     into user_profiles type-checks. Storing s.id would fail with
     "invalid input syntax for type uuid". */
  var supplierOpts = useMemo(function () {
    return (suppliers || [])
      .filter(function (s) { return s.isActive !== false && s._uuid; })
      .map(function (s) {
        return {
          value: s._uuid,
          label: (s.shortName || s.fullName || s.name || s.id) + (s.initials ? " (" + s.initials + ")" : ""),
        };
      });
  }, [suppliers]);

  /* Add-mode dropdown shows the 4 active roles. Edit-mode also exposes
     the legacy `shipper` role so existing shipper users don't get
     forced into a different bucket on save. */
  var roleOpts = isEdit
    ? [
        { value: "admin", label: t.role_admin },
        { value: "shipper", label: t.role_shipper },
        { value: "logistica", label: t.role_logistica },
        { value: "operacion", label: t.role_operacion },
        { value: "gerencial", label: t.role_gerencial },
        { value: "compliance", label: t.role_compliance },
      ]
    : [
        { value: "admin", label: t.role_admin },
        { value: "logistica", label: t.role_logistica },
        { value: "operacion", label: t.role_operacion },
        { value: "gerencial", label: t.role_gerencial },
        { value: "compliance", label: t.role_compliance },
      ];

  var phoneSerialized = serializePhone(dialToCountryCode(form.phoneCountry || "52"), form.phoneArea, form.phoneNumber);
  var commitPhone = function (s) {
    var p = parsePhoneShared(s);
    set("phoneCountry", countryCodeToDialDigits(p.country));
    set("phoneArea", p.area);
    set("phoneNumber", p.local);
  };

  return (
    <DraggableModal title={isEdit ? t.editTitle : t.addTitle} onClose={busy ? function () {} : onCancel} initialWidth={620} initialHeight={Math.min(740, window.innerHeight - 60)}>
      <div style={{ overflow: "auto", flex: 1, padding: "18px 22px" }}>
        {errBanner && (
          <div style={{ background: "rgba(231,76,60,0.10)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "var(--color-danger)", fontWeight: 500, marginBottom: 14 }}>
            {t.err_prefix}{errBanner}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Section title={t.section_account}>
            <Field label={t.f_email} sub={isEdit ? null : t.f_email_sub} required={!isEdit} error={errors.email}>
              <input type="email" autoComplete="off" disabled={isEdit} value={form.email}
                onChange={function (e) { set("email", e.target.value); }}
                style={Object.assign({}, inp, isEdit ? { background: "var(--color-bg)", color: "var(--color-text-muted)" } : {}, errors.email ? errS : {})} />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label={t.f_firstName} required error={errors.name}>
                <input type="text" value={form.firstName} onChange={function (e) { set("firstName", e.target.value); }} style={Object.assign({}, inp, errors.name ? errS : {})} />
              </Field>
              <Field label={t.f_lastName} required error={errors.name}>
                <input type="text" value={form.lastName} onChange={function (e) { set("lastName", e.target.value); }} style={Object.assign({}, inp, errors.name ? errS : {})} />
              </Field>
            </div>
          </Section>

          <Section title={t.section_role}>
            <Field label={t.f_role} required>
              <SearchSelect value={form.role} onChange={function (v) {
                /* Switching role swaps in that role's default permission
                   map. Admin gets an empty map since hasPermission()
                   short-circuits to true for admin. */
                setForm(function (f) {
                  return Object.assign({}, f, {
                    role: v,
                    permissions: v === "admin" ? {} : defaultsForRole(v),
                  });
                });
                if (errors.role) setErrors(function (e) { return Object.assign({}, e, { role: null }); });
              }} options={roleOpts} />
            </Field>
            {form.role !== "admin" && (
              <Field label={t.f_supplier} sub={t.f_supplierSub} required error={errors.supplier}>
                <SearchSelect value={form.supplierId} onChange={function (v) { set("supplierId", v); }} options={supplierOpts} placeholder="—" error={!!errors.supplier} />
              </Field>
            )}
          </Section>

          {isEdit && (
            <Section title={t.section_contact}>
              <Field label={t.f_phone} sub={t.f_phoneSub}>
                <PhoneField value={phoneSerialized} onChange={commitPhone} lang={lang} inputStyle={inp} />
              </Field>
            </Section>
          )}

          {/* Granular permissions — hidden for admin (admin gets full
              access via hasPermission short-circuit) and for invite
              mode (new users get the role's defaults applied by
              accept-invitation.js; admin can fine-tune after the user
              completes registration). */}
          {isEdit && form.role && form.role !== "admin" && (
            <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid var(--color-border)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text)" }}>{t.permissions}</div>
                  <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>{t.permissionsSub}</div>
                </div>
                <button type="button" onClick={function () {
                  var confirmMsg = (lang === "es" ? "Esto restablecerá todos los permisos a los valores por defecto del rol " : "This will reset all permissions to the role defaults of ") + form.role + ". ¿Continuar?";
                  if (window.confirm(confirmMsg)) {
                    setForm(function (f) { return Object.assign({}, f, { permissions: defaultsForRole(form.role) }); });
                  }
                }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>
                  ↻ {t.resetToDefaults}
                </button>
              </div>

              {PERMISSIONS.map(function (group, gIdx) {
                var subLabel = lang === "es" ? group.submenu.es : group.submenu.en;
                var menuLabel = lang === "es" ? group.menu.es : group.menu.en;
                return (
                  <div key={gIdx} style={{ marginTop: 12, padding: 12, background: "var(--color-bg-card-solid)", borderRadius: 8, border: "1px solid var(--color-border)" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{menuLabel}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text)", marginTop: 2 }}>{subLabel}</div>
                    <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                      {group.items.map(function (item) {
                        var label = lang === "es" ? item.es : item.en;
                        var checked = !!(form.permissions && form.permissions[item.key]);
                        return (
                          <label key={item.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer", padding: "4px 6px", borderRadius: 4 }}>
                            <input type="checkbox" checked={checked} onChange={function (e) {
                              var v = e.target.checked;
                              setForm(function (f) {
                                return Object.assign({}, f, { permissions: Object.assign({}, f.permissions || {}, { [item.key]: v }) });
                              });
                            }} />
                            <span>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: "12px 22px", borderTop: "1px solid var(--color-border)", background: "var(--color-bg)" }}>
        <button onClick={onCancel} disabled={busy} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: busy ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600 }}>{t.cancel}</button>
        <button onClick={save} disabled={busy} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: busy ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: busy ? 0.7 : 1 }}>{busy ? t.saving : (isEdit ? t.save : t.sendInvitation)}</button>
      </div>
    </DraggableModal>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
   Top-level
   ─────────────────────────────────────────────────────────────────────────── */
function RolePermissionsEditor({ lang, onClose }) {
  var ROLES = ["logistica", "operacion", "gerencial", "compliance"];
  var ROLE_LABELS = {
    logistica: lang === "es" ? "Logística" : "Logistics",
    operacion: lang === "es" ? "Operaciones" : "Operations",
    gerencial: lang === "es" ? "Gerencial" : "Management",
    compliance: lang === "es" ? "Cumplimiento" : "Compliance",
  };
  var [roleSel, setRoleSel] = useState("logistica");
  var [maps, setMaps] = useState(null);
  var [busy, setBusy] = useState(false);
  var [toast, setToast] = useState("");
  useEffect(function () {
    var alive = true;
    supabase.from("role_permissions").select("role, permissions").then(function (res) {
      if (!alive) return;
      var rows = (res && res.data) || [];
      var byRole = {};
      rows.forEach(function (r) { byRole[r.role] = r.permissions || {}; });
      var next = {};
      ROLES.forEach(function (role) {
        var base = defaultsForRole(role);
        var saved = byRole[role];
        if (saved) {
          var merged = {};
          Object.keys(base).forEach(function (k) { merged[k] = saved[k] === true; });
          next[role] = merged;
        } else {
          next[role] = base;
        }
      });
      setMaps(next);
    });
    return function () { alive = false; };
  }, []);
  function toggle(role, key) {
    setMaps(function (prev) {
      var copy = Object.assign({}, prev);
      var rm = Object.assign({}, copy[role]);
      rm[key] = !rm[key];
      copy[role] = rm;
      return copy;
    });
  }
  function resetRole(role) {
    setMaps(function (prev) {
      var copy = Object.assign({}, prev);
      copy[role] = defaultsForRole(role);
      return copy;
    });
  }
  async function saveRole(role) {
    if (!maps) return;
    setBusy(true);
    try {
      var res = await supabase.from("role_permissions").upsert({ role: role, permissions: maps[role], updated_at: new Date().toISOString() }, { onConflict: "role" });
      if (res.error) throw res.error;
      setToast(lang === "es" ? "Permisos del rol guardados." : "Role permissions saved.");
      setTimeout(function () { setToast(""); }, 2500);
    } catch (e) {
      setToast((lang === "es" ? "Error: " : "Error: ") + ((e && e.message) || String(e)));
    }
    setBusy(false);
  }
  return (
    <DraggableModal title={lang === "es" ? "Permisos por Rol" : "Role Permissions"} onClose={busy ? function () {} : onClose} initialWidth={680} initialHeight={Math.min(740, window.innerHeight - 60)}>
      <div style={{ overflow: "auto", flex: 1, padding: "16px 20px" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {ROLES.map(function (role) {
            var active = role === roleSel;
            return (
              <button key={role} onClick={function () { setRoleSel(role); }} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: active ? "var(--color-accent)" : "transparent", color: active ? "var(--color-accent-text)" : "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
                {ROLE_LABELS[role]}
              </button>
            );
          })}
        </div>
        {!maps && <div style={{ padding: 30, textAlign: "center", color: "var(--color-text-muted)" }}>{lang === "es" ? "Cargando…" : "Loading…"}</div>}
        {maps && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{lang === "es" ? "Activa o desactiva los permisos por defecto de este rol." : "Toggle the default permissions for this role."}</div>
              <button onClick={function () { resetRole(roleSel); }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 11, fontWeight: 600, color: "var(--color-text-muted)", whiteSpace: "nowrap" }}>↻ {lang === "es" ? "Valores originales" : "Original defaults"}</button>
            </div>
            {PERMISSIONS.map(function (group, gIdx) {
              var menuLabel = lang === "es" ? group.menu.es : group.menu.en;
              var subLabel = lang === "es" ? group.submenu.es : group.submenu.en;
              return (
                <div key={gIdx} style={{ marginTop: 12, padding: 12, background: "var(--color-bg-card-solid)", borderRadius: 8, border: "1px solid var(--color-border)" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{menuLabel}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text)", marginTop: 2 }}>{subLabel}</div>
                  <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                    {group.items.map(function (item) {
                      var on = maps[roleSel] && maps[roleSel][item.key] === true;
                      return (
                        <label key={item.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--color-text)", cursor: "pointer", padding: "4px 6px" }}>
                          <input type="checkbox" checked={!!on} onChange={function () { toggle(roleSel, item.key); }} />
                          {lang === "es" ? item.es : item.en}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
              <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)" }}>{lang === "es" ? "Cerrar" : "Close"}</button>
              <button disabled={busy} onClick={function () { saveRole(roleSel); }} style={{ padding: "8px 18px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: busy ? "wait" : "pointer", fontSize: 12, fontWeight: 700 }}>{busy ? "…" : (lang === "es" ? "Guardar " + ROLE_LABELS[roleSel] : "Save " + ROLE_LABELS[roleSel])}</button>
            </div>
          </div>
        )}
        {toast && <div style={{ marginTop: 12, fontSize: 12, color: "var(--color-accent)", textAlign: "center" }}>{toast}</div>}
      </div>
    </DraggableModal>
  );
}

export default function UserManagement({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang === "en" ? "en" : "es"];

  var [users, setUsers] = useState(null);  /* null while loading */
  var [invitations, setInvitations] = useState([]);
  var [suppliers, setSuppliers] = useState([]);
  var [editing, setEditing] = useState(null);  /* user object or "new" */
  var [editingInv, setEditingInv] = useState(null);  /* invitation object */
  var [toast, setToast] = useState("");
  var [error, setError] = useState("");
  var [currentUid, setCurrentUid] = useState("");
  var [showRolePerms, setShowRolePerms] = useState(false);

  var load = useCallback(async function () {
    setError("");
    try {
      var list = await listAllProfiles();
      setUsers(list);
    } catch (e) {
      setError((e && e.message) || String(e));
      setUsers([]);
    }
  }, []);

  var loadInvitations = useCallback(async function () {
    var res = await supabase
      .from("user_invitations")
      .select("*")
      .eq("status", "pending")
      .order("created_at", { ascending: false });
    if (!res.error) setInvitations(res.data || []);
  }, []);

  useEffect(function () {
    load();
    loadInvitations();
    loadFromSupabase("suppliers")
      .then(function (rows) { setSuppliers(rows || []); })
      .catch(function () { setSuppliers([]); });
    // Capture the current user id so we can label our own row.
    supabase.auth.getSession().then(function (r) {
      var uid = r && r.data && r.data.session && r.data.session.user && r.data.session.user.id;
      if (uid) setCurrentUid(uid);
    });
  }, [load, loadInvitations]);

  useEffect(function () {
    if (!toast) return;
    var id = setTimeout(function () { setToast(""); }, 5000);
    return function () { clearTimeout(id); };
  }, [toast]);

  var supplierName = function (id) {
    if (!id) return "—";
    /* `id` is the uuid stored in user_profiles.supplier_id. Match it
       against suppliers._uuid; fall back to legacy id for any
       record that might predate the uuid-in-profile convention. */
    var s = suppliers.find(function (x) { return String(x._uuid) === String(id) || String(x.id) === String(id); });
    return s ? (s.shortName || s.fullName || s.name || "—") : "—";
  };

  var fmtPhone = function (u) {
    var a = (u.phone_area_code || "").trim();
    var n = (u.phone_number || "").trim();
    if (!a && !n) return "—";
    var c = (u.phone_country_code || "").trim();
    return [c ? "+" + c : "", a, n].filter(Boolean).join(" ");
  };

  var handleSave = function (result) {
    setEditing(null);
    setEditingInv(null);
    load();
    if (result.kind === "invited") {
      loadInvitations();
      setToast(t.invitationSent);
    } else if (result.kind === "invitationUpdated") {
      loadInvitations();
      setToast(t.invitationUpdated);
    } else {
      setToast(t.updated);
    }
  };

  var revokeInvitation = async function (id) {
    if (!window.confirm(t.confirmRevoke)) return;
    try {
      var res = await supabase.from("user_invitations").update({ status: "revoked" }).eq("id", id);
      if (res.error) throw new Error(res.error.message);
      await loadInvitations();
      setToast(t.invitationRevoked);
    } catch (e) {
      setError((e && e.message) || String(e));
    }
  };

  var fmtTs = function (s) {
    if (!s) return "—";
    try { return new Date(s).toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { year: "numeric", month: "short", day: "2-digit" }); }
    catch (e) { return String(s); }
  };

  var handleDeleteUser = async function (u) {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      var res = await fetch("/.netlify/functions/delete-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: u.id }),
      });
      var data = await res.json().catch(function () { return {}; });
      if (!res.ok || !data.success) {
        setError(data.error || ("HTTP " + res.status));
        return;
      }
      /* Reload both lists — the user might have had pending invitations
         tied to their email that the function cleaned up too. */
      load();
      loadInvitations();
      setToast(t.userDeleted);
    } catch (e) {
      setError((e && e.message) || String(e));
    }
  };

  var handleToggleActive = async function (u) {
    var msg = u.is_active ? t.confirmDeactivate : t.confirmActivate;
    if (!window.confirm(msg)) return;
    try {
      await setUserActive(u.id, !u.is_active);
      load();
    } catch (e) {
      setError((e && e.message) || String(e));
    }
  };

  /* (b) Admin sets a new password directly. window.prompt is a zero-UI
     capture — the admin types a temporary password and dictates it to
     the user. Password rules mirror reset-password.js / admin-set-password
     exactly so client and server never disagree. The change runs
     server-side because only the service role key may set another
     user's password. */
  var handleSetPassword = async function (u) {
    var pwd = window.prompt(t.promptNewPassword);
    if (pwd === null) return;
    pwd = String(pwd);
    var ok = pwd.length >= 8 && /[A-Z]/.test(pwd) && /[a-z]/.test(pwd) && /[0-9]/.test(pwd) && /[^A-Za-z0-9]/.test(pwd);
    if (!ok) { setError(t.pwdWeak); return; }
    try {
      var res = await fetch("/.netlify/functions/admin-set-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: u.id, password: pwd }),
      });
      var data = await res.json().catch(function () { return {}; });
      if (!res.ok || !data.success) { setError(data.error || ("HTTP " + res.status)); return; }
      setToast(t.passwordSet);
    } catch (e) {
      setError((e && e.message) || String(e));
    }
  };

  /* (c) Send the user a reset email so they pick their own password.
     send-password-reset resolves by USERNAME, so a user without one
     yet can't receive it — guard up front instead of silently no-op. */
  var handleSendReset = async function (u) {
    if (!u.username) { setError(t.noUsernameReset); return; }
    if (!window.confirm(t.confirmSendReset)) return;
    try {
      var res = await fetch("/.netlify/functions/send-password-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: u.username }),
      });
      var data = await res.json().catch(function () { return {}; });
      if (!res.ok || !data.success) { setError(data.error || ("HTTP " + res.status)); return; }
      setToast(t.resetEmailSent);
    } catch (e) {
      setError((e && e.message) || String(e));
    }
  };

  var th = { textAlign: "left", padding: "10px 12px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-secondary)", borderBottom: "2px solid var(--color-border)" };
  var td = { padding: "10px 12px", borderBottom: "1px solid var(--color-border)", fontSize: 13 };

  return (
    <div style={{ padding: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{t.title}</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-muted)" }}>{t.subtitle}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.refresh}</button>
          <button onClick={function () { setShowRolePerms(true); }} style={{ padding: "8px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{lang === "es" ? "Permisos por Rol" : "Role Permissions"}</button>
          <button onClick={function () { setEditing("new"); }} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "var(--color-accent-text)", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{t.addUser}</button>
        </div>
      </div>

      {error && (
        <div style={{ background: "rgba(231,76,60,0.10)", border: "1px solid rgba(231,76,60,0.25)", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "var(--color-danger)", fontWeight: 500, marginBottom: 14 }}>
          {t.err_prefix}{error}
        </div>
      )}

      {users === null ? (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13 }}>{t.loading}</div>
      ) : users.length === 0 ? (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13, fontStyle: "italic" }}>{t.noUsers}</div>
      ) : (
        <div style={{ borderRadius: 10, border: "1px solid var(--color-border)", overflow: "hidden", background: "var(--color-bg-card)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>{t.columns.name}</th>
                <th style={th}>{t.columns.email}</th>
                <th style={th}>{t.columns.role}</th>
                <th style={th}>{t.columns.supplier}</th>
                <th style={th}>{t.columns.phone}</th>
                <th style={th}>{t.columns.status}</th>
                <th style={Object.assign({}, th, { textAlign: "right" })}>{t.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {users.map(function (u) {
                var isMe = u.id === currentUid;
                return (
                  <tr key={u.id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{[u.first_name, u.last_name].filter(Boolean).join(" ") || "—"} {isMe && <span style={{ color: "var(--color-text-muted)", fontWeight: 400, fontSize: 11 }}>{t.you}</span>}</div>
                    </td>
                    <td style={Object.assign({}, td, { fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "var(--color-text-secondary)" })}>
                      <div>{u.email}</div>
                      {u.masked_email && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2 }}>{u.masked_email}</div>}
                    </td>
                    <td style={td}><RoleBadge role={u.role} t={t} /></td>
                    <td style={td}>{u.role === "admin" ? "—" : supplierName(u.supplier_id)}</td>
                    <td style={Object.assign({}, td, { fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "var(--color-text-secondary)" })}>{fmtPhone(u)}</td>
                    <td style={td}><StatusBadge active={u.is_active} t={t} /></td>
                    <td style={Object.assign({}, td, { textAlign: "right" })}>
                      <div style={{ display: "inline-flex", gap: 6 }}>
                        <button onClick={function () { setEditing(u); }} style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.edit}</button>
                        {!isMe && (
                          <>
                            <button onClick={function () { handleSetPassword(u); }} style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.setPwdTitle}</button>
                            <button onClick={function () { handleSendReset(u); }} style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.sendResetTitle}</button>
                            <button onClick={function () { handleToggleActive(u); }} style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid " + (u.is_active ? "rgba(220,38,38,0.3)" : "rgba(16,185,129,0.3)"), background: u.is_active ? "rgba(220,38,38,0.06)" : "rgba(16,185,129,0.06)", color: u.is_active ? "#DC2626" : "#059669", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{u.is_active ? t.deactivate : t.activate}</button>
                            <button onClick={function () { handleDeleteUser(u); }} style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid rgba(231,76,60,0.4)", background: "rgba(231,76,60,0.08)", color: "var(--color-danger)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.deleteUser}</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending invitations — anything created via "+ Invite User" but
          not yet completed by the recipient. Revoking flips status to
          'revoked' so the registration link stops working. */}
      <div style={{ marginTop: 28 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>{t.pendingInvitations}</h3>
        {invitations.length === 0 ? (
          <div style={{ padding: "20px", textAlign: "center", color: "var(--color-text-muted)", fontSize: 13, fontStyle: "italic", borderRadius: 10, border: "1px dashed var(--color-border)" }}>{t.noInvitations}</div>
        ) : (
          <div style={{ borderRadius: 10, border: "1px solid var(--color-border)", overflow: "hidden", background: "var(--color-bg-card)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>{t.columns.email}</th>
                  <th style={th}>{t.columns.name}</th>
                  <th style={th}>{t.columns.role}</th>
                  <th style={th}>{t.columns.supplier}</th>
                  <th style={th}>{t.sentAt}</th>
                  <th style={th}>{t.expiresAt}</th>
                  <th style={Object.assign({}, th, { textAlign: "right" })}>{t.columns.actions}</th>
                </tr>
              </thead>
              <tbody>
                {invitations.map(function (inv) {
                  return (
                    <tr key={inv.id} onClick={function () { setEditingInv(inv); }} style={{ cursor: "pointer" }}>
                      <td style={Object.assign({}, td, { fontFamily: "'JetBrains Mono', monospace", fontSize: 12 })}>{inv.email}</td>
                      <td style={td}>{[inv.first_name, inv.last_name].filter(Boolean).join(" ") || "—"}</td>
                      <td style={td}><RoleBadge role={inv.role} t={t} /></td>
                      <td style={td}>{inv.role === "admin" ? "—" : supplierName(inv.supplier_id)}</td>
                      <td style={Object.assign({}, td, { fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "var(--color-text-secondary)" })}>{fmtTs(inv.created_at)}</td>
                      <td style={Object.assign({}, td, { fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "var(--color-text-secondary)" })}>{fmtTs(inv.expires_at)}</td>
                      <td style={Object.assign({}, td, { textAlign: "right" })} onClick={function (e) { e.stopPropagation(); }}>
                        <button onClick={function (e) { e.stopPropagation(); revokeInvitation(inv.id); }} style={{ padding: "5px 10px", borderRadius: 5, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#DC2626", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.revokeInvitation}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <UserForm
          user={editing === "new" ? null : editing}
          suppliers={suppliers}
          t={t}
          lang={lang}
          onSave={handleSave}
          onCancel={function () { setEditing(null); }}
        />
      )}

      {editingInv && (
        <UserForm
          invitation={editingInv}
          suppliers={suppliers}
          t={t}
          lang={lang}
          onSave={handleSave}
          onCancel={function () { setEditingInv(null); }}
        />
      )}

      {showRolePerms && (
        <RolePermissionsEditor lang={lang} onClose={function () { setShowRolePerms(false); }} />
      )}

      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: "var(--color-accent)", color: "var(--color-accent-text)",
          padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 500,
          zIndex: 10001, boxShadow: "0 8px 24px var(--color-shadow)",
          maxWidth: 480,
        }}>{toast}</div>
      )}
    </div>
  );
}
